import styled, { css } from 'styled-components';
import { themeGet, space, SpaceProps, minHeight, height } from 'styled-system';

import { basicTheme } from 'src/theme';
import { controlSize, disabledStyles } from 'src/kit/styles';
import { LayoutBox, LayoutBoxProps } from '../Box';
import { StyledInput } from '../StyledInput';

export type StyledContainerProps = LayoutBoxProps;

export const StyledContainer = styled(LayoutBox)`
  position: relative;
  ${height}

  .group {
    padding: 10px 10px 0;
    width: 100%;
  }
`;

export const StyledComboboxControl = styled(StyledInput).attrs(props => ({
  as: 'div',
  className: props.disabled ? 'disabled' : '',
  minHeight: props.size
}))<{ isOpen: boolean; classicAppearance?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  outline: none;
  height: ${props => props.classicAppearance && 'auto'};
  ${minHeight};
  &:focus-within {
    border-color: ${themeGet('colors.combobox.focusBorder')};
  }

  &.disabled {
    ${disabledStyles};
  }

  > .chevron {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%) ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0)')};
    color: ${themeGet('colors.textSecondary')};
  }

  .search-input {
    padding: 0;
    flex: 1;
    height: 100%;
  }
`;

export const StyledMenuItem = styled.li<{ isHighlighted?: boolean }>`
  overflow: hidden;
  padding: 0 20px;
  display: flex;
  align-items: center;
  height: 34px;
  line-height: 34px;
  color: ${themeGet('colors.textSecondary')};
  user-select: none;
  white-space: nowrap;
  background-color: ${props => props.isHighlighted && themeGet('colors.combobox.selected')};
  &:hover {
    background-color: ${themeGet('colors.combobox.selected')};
  }
  &[role='option'] {
    cursor: pointer;
  }
`;

export const StyledInnerSelectedItem = styled.div`
  overflow: hidden;
  position: relative;
  margin: 4px 10px 4px 0;
  padding: 0 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  border: 1px solid ${themeGet('colors.textSecondary')};
  border-radius: 4px;
  background-color: ${themeGet('colors.white')};
  color: ${themeGet('colors.textSecondary')};
`;

export type StyledMenuProps = {
  size: keyof typeof basicTheme.controlSizes;
  itemsCount: number;
  classicAppearance?: boolean;
};

export const StyledMenu = styled.ul<StyledMenuProps>`
  margin: 0;
  padding: 5px 0;
  list-style: none;
  outline: none;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  background-color: ${themeGet('colors.white')};
  border-radius: 4px;
  box-shadow: ${themeGet('shadows.dropdownMenu')};
  ${props =>
    props.classicAppearance &&
    css`
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;

      > ${StyledMenuItem} {
        flex: ${props => (props.itemsCount > 7 ? '0 1 50%' : '1 1 100%')};
      }
    `}
`;

export const StyledOuterSelectedItem = styled.div<SpaceProps & { size: string }>`
  margin: 0 10px 10px 0;
  display: flex;
  align-items: center;
  max-width: 100%;
  border-radius: 20px;
  background-color: ${themeGet('colors.multiSelectItem.bg')};
  color: ${themeGet('colors.multiSelectItem.color')};
  .close-icon {
    color: ${themeGet('colors.multiSelectItem.closeIcon')};
    &:hover {
      opacity: 0.8;
    }
  }
  ${controlSize};
  ${space};
`;
StyledOuterSelectedItem.defaultProps = {
  size: 'md'
};
